import React, { MouseEvent, useCallback, useMemo, useRef } from 'react';
import { Box, Grid, Typography, Button } from '@mui/material';
import { isFinite } from 'lodash';
import { useRouter } from 'next/router';
import { useMountedState } from 'react-use';
import Skeleton from 'react-loading-skeleton';
import { midnightOfDate, trackEvent } from '@surfline/web-common';
import { CalendarIcon } from '@wavetrak/theme';

import ForecastGraphsSunlightTable from 'components/ForecastGraphsSunlightTable';
import TideChartContainer from 'components/ForecastGraphTide/components/TideChartContainer';
import TideReadingsTable from 'components/TideReadingsTable';
import GraphTooltipProvider from 'containers/ForecastDataVisualizer/GraphTooltips/GraphTooltipProvider';
import TideHeightTooltip from 'containers/ForecastDataVisualizer/GraphTooltips/TideHeightTooltip';
import useTimestamps from 'containers/ForecastDataVisualizer/hooks/useTimestamps';
import useTooltipData from 'containers/ForecastDataVisualizer/hooks/useTooltipData';
import { useMaxWidthTabletLarge, useMinWidthDesktopSmall } from 'hooks/useMediaQueries';
import { MoveTideLabel, type TideData } from 'types/tide';
import type { SunlightTimes } from 'types/sunlightTimes';
import type { GraphId } from 'containers/ForecastDataVisualizer/GraphTooltips/GraphTooltipProvider/GraphTooltipProvider';

import classNames from 'classnames';
import WavetrakLink from 'components/WavetrakLink';
import styles from './TimeTableContainer.module.scss';

interface Props {
  tide: TideData;
  toggleDayExpanded: () => void;
  sunlightTimes: SunlightTimes;
  daysToDisplay: Array<number>;
  dateFormat?: 'MDY' | 'DMY';
  dayIndex?: number;
  spotName: string | undefined;
}

const TimeTableContainer: React.FC<Props> = ({
  tide,
  toggleDayExpanded,
  sunlightTimes,
  daysToDisplay,
  dateFormat = 'MDY',
  dayIndex = 0,
  spotName = undefined,
}) => {
  const router = useRouter();
  const { spotId, spotSlug } = router.query;
  const isMinWidthDesktopSmall = useMinWidthDesktopSmall();
  const expandCollapseHandler = useCallback(() => {
    toggleDayExpanded();
  }, [toggleDayExpanded]);
  const hasTide = useMemo(
    () => Boolean(tide?.days?.length || tide.loading || tide.error),
    [tide?.days?.length, tide.error, tide.loading],
  );

  const flatData = useMemo(
    () =>
      daysToDisplay
        ?.map((day) => tide.days && tide?.days[day])
        .filter((day) => day)
        .flat(),
    [daysToDisplay, tide?.days],
  );

  const onClickTideChartsHandler = useCallback((event: MouseEvent) => {
    // stop the event that collapses the table container in expandCollapseHandler
    event.stopPropagation();
  }, []);

  const graphContainerRef = useRef<HTMLDivElement | null>(null);
  const utcOffset = flatData?.[0]?.utcOffset;
  const midnightOfStartDate = useMemo(
    () => new Date(midnightOfDate(utcOffset, flatData?.[0]?.timestamp) * 1000),
    [flatData, utcOffset],
  );
  const isGraphFullBleed = useMaxWidthTabletLarge();

  const isMounted = useMountedState();

  const { initialXPercentage, xScale } = useTimestamps({
    firstDayIndex: 0,
    graphContainerRef,
    isMounted: isMounted(),
    midnightOfStartDate,
    lastDayIndex: 0,
    additionalOffset: false,
  });

  const graphWidth = useMemo(() => {
    const range = xScale?.range();
    return range?.length === 2 ? range[1] - range[0] : 0;
  }, [xScale]);

  const trackGraphScrubbed = useCallback(
    (graphId: GraphId, additionalSegmentProps: { [key: string]: string | number }) =>
      trackEvent('Graph Scrubbed', {
        forecastEnd: dayIndex + 1,
        forecastStart: dayIndex + 1,
        graphType: graphId,
        moduleName: 'forecast table',
        pageName: 'Spot Report',
        spotId,
        spotName,
        ...additionalSegmentProps,
      }),
    // Important: the flag used to skip tracking is reset when these props change
    [dayIndex, spotId, spotName],
  );

  const tideDaysToDisplay = useMemo(() => [dayIndex], [dayIndex]);
  const tooltipData = useTooltipData({
    daysToDisplay: tideDaysToDisplay,
    initialXPercentage,
    isGraphFullBleed,
    tide,
    xScale,
  });

  if (!tide || !flatData) return <Skeleton />;

  return (
    <Box
      className={styles.timeTableContainer}
      data-testid="time-table-container"
      onClick={expandCollapseHandler}
    >
      {hasTide ? (
        <>
          <Box className={styles.tideGraphHeading}>
            <Typography variant="callout1">
              Tides{' '}
              {tide.units?.tideHeight && (
                <Typography
                  variant="callout1"
                  component="span"
                  className={styles.tideGraphHeadingUnits}
                >
                  ({tide.units?.tideHeight})
                </Typography>
              )}
            </Typography>
            {tide.tideLocation?.name && (
              <Typography
                className={styles.tideGraphSubheading}
                component="p"
                data-testid="table-tide-chart-typography-text"
                variant="caption1"
              >
                {tide.tideLocation.name}
              </Typography>
            )}
            {!tide.error && (
              <Button
                className={styles.tideGraphButton}
                component={WavetrakLink}
                data-testid="table-tide-chart-button"
                href={`/tide-charts/${spotSlug}/${spotId}`}
                onClick={onClickTideChartsHandler}
                size="small"
                startIcon={<CalendarIcon />}
                variant="secondary"
              >
                Tide calendar
              </Button>
            )}
          </Box>
          <Grid container spacing={2} data-testid="time-table-contents-tide-exists">
            {tide?.error ? (
              <Grid item mobile={12} desktopSm={8} desktopLg={9}>
                <TideReadingsTable
                  errorBoundaryClass={styles.tideReadingsError}
                  tide={tide?.days?.[dayIndex]}
                  units={tide?.units}
                  utcOffset={tide?.utcOffset}
                  error={tide?.error}
                  loading={tide?.loading}
                  isTableMode
                />
              </Grid>
            ) : (
              <Grid item mobile={12} desktopSm={8} desktopLg={9}>
                <div className={styles.graphData} ref={graphContainerRef}>
                  {isFinite(tooltipData?.tide?.data?.current?.height) && (
                    <TideHeightTooltip
                      tideHeight={tooltipData?.tide?.data?.current?.height}
                      units={tide.units?.tideHeight}
                      leftPosition={tooltipData?.tide?.leftPosition}
                      graphWidth={graphWidth}
                    />
                  )}
                  <GraphTooltipProvider
                    customStyle={styles.tideGraphVerticalBar}
                    customStyleNow={styles.tideGraphVerticalBarNow}
                    graphId="tide"
                    graphWidth={graphWidth}
                    leftNowPosition={tooltipData?.tide?.leftNowPosition}
                    leftPosition={tooltipData?.tide?.leftPosition}
                    renderTooltip={tooltipData.renderTooltip}
                    timeLabel={tooltipData?.tide?.timeLabel}
                    trackGraphScrubbed={trackGraphScrubbed}
                  >
                    <TideChartContainer
                      tide={tide}
                      sunlightTimes={sunlightTimes}
                      flatData={flatData}
                      startTimestamp={flatData?.[0]?.timestamp}
                      endTimestamp={flatData?.[flatData.length - 1]?.timestamp}
                      dateFormat={dateFormat}
                      isTableMode
                      dayIndex={dayIndex}
                      moveTideLabel={MoveTideLabel.BOTH}
                      showNowPosition={false}
                    />
                  </GraphTooltipProvider>
                </div>
              </Grid>
            )}
            <Grid item mobile={12} desktopSm={4} desktopLg={3}>
              <Box className={styles.sunlightTable} data-testid="sunlight-table">
                <ForecastGraphsSunlightTable
                  day={dayIndex}
                  isColumn={isMinWidthDesktopSmall}
                  sunlightTimes={sunlightTimes}
                />
              </Box>
            </Grid>
          </Grid>
        </>
      ) : (
        <Box
          className={classNames(styles.sunlightTable, styles.sunlightTableNoTide)}
          data-testid="sunlight-table-no-tide"
        >
          <ForecastGraphsSunlightTable
            day={dayIndex}
            isColumn={isMinWidthDesktopSmall}
            sunlightTimes={sunlightTimes}
          />
        </Box>
      )}
    </Box>
  );
};

export default TimeTableContainer;
